import React from 'react';
import {
  aggregateStats,
  computeHour,
  fmt,
  hasHourContent,
  HOURS_PER_TAB,
  DAY_LABELS,
  HOUR_SECONDS,
  GAP_COLOR,
} from './defaults';

// Aggregate pie chart across all filled clocks + interactive weekly
// mosaic (7 × 24 mini clocks). Click any filled mini clock to jump to
// that hour in edit mode. `onHourClick(hourIndex)` is optional — if not
// provided the mosaic is still shown but read-only.
export default function OverviewPanel({ state, onHourClick }) {
  const { items, totalUsed } = aggregateStats(state);
  const size = 380;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;

  const totalDenom = Math.max(totalUsed, 1);
  let ang = 0;
  const slices = items.map((it, i) => {
    const a0 = ang;
    const a1 = ang + (2 * Math.PI * it.sec) / totalDenom;
    ang = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.sin(a0);
    const y0 = cy - r * Math.cos(a0);
    const x1 = cx + r * Math.sin(a1);
    const y1 = cy - r * Math.cos(a1);
    return (
      <path
        key={i}
        d={`M${cx} ${cy} L${x0} ${y0} A${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`}
        fill={it.color}
        stroke="#fff"
        strokeWidth="1"
      />
    );
  });

  const totalFilled = state.hours.reduce(
    (n, h) => n + (hasHourContent(h, state.categories, state.speech_types) ? 1 : 0),
    0
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 text-slate-800">
      <h3 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide mb-3">
        Συνολικά Στατιστικά — Και τα 168 Clocks
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="flex justify-center">
          {items.length === 0 ? (
            <div className="w-full text-center py-12 text-gray-400 text-sm">
              Δεν έχεις προσθέσει ακόμα κατηγορίες σε κανένα clock.
            </div>
          ) : (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{slices}</svg>
          )}
        </div>
        <div>
          <table className="w-full text-xs border-collapse text-slate-800">
            <thead>
              <tr style={{ backgroundColor: '#1B2A4A' }}>
                <th style={{ color: '#ffffff', padding: '6px 8px', textAlign: 'left' }}>Κατηγορία</th>
                <th style={{ color: '#ffffff', padding: '6px 8px', width: '64px' }}>Χρόνος</th>
                <th style={{ color: '#ffffff', padding: '6px 8px', width: '56px' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-2 py-1 flex items-center gap-2 text-slate-800">
                    <span
                      className="inline-block w-3 h-3 rounded-sm border border-black/10"
                      style={{ background: it.color }}
                    />
                    {it.name}
                  </td>
                  <td className="px-2 py-1 font-mono text-center text-slate-800">{fmt(it.sec)}</td>
                  <td className="px-2 py-1 font-mono text-center text-slate-800">
                    {((it.sec / totalDenom) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
              {items.length > 0 && (
                <tr className="bg-slate-200 font-bold text-slate-900">
                  <td className="px-2 py-1">Σύνολο</td>
                  <td className="px-2 py-1 font-mono text-center">{fmt(totalUsed)}</td>
                  <td className="px-2 py-1 font-mono text-center">100%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- Weekly Mosaic ---- */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h4 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide">
            Εβδομαδιαίο Mosaic — {totalFilled}/168 ρολόγια συμπληρωμένα
          </h4>
          <p className="text-xs text-slate-500">
            👆 Κάνε <b>κλικ</b> σε ένα συμπληρωμένο ρολόι για να ανοίξει στη θέση του για επεξεργασία.
          </p>
        </div>

        <div className="overflow-x-auto">
          <div
            className="inline-grid gap-[3px]"
            style={{ gridTemplateColumns: `74px repeat(${HOURS_PER_TAB}, 40px)`, minWidth: 74 + HOURS_PER_TAB * 43 }}
          >
            <div />
            {Array.from({ length: HOURS_PER_TAB }).map((_, h) => (
              <div key={`hd-${h}`} className="text-[10px] text-center text-slate-500 font-mono pb-1">
                {String(h).padStart(2, '0')}
              </div>
            ))}
            {DAY_LABELS.map((day, di) => (
              <React.Fragment key={di}>
                <div className="text-[11px] font-bold text-[#1B2A4A] flex items-center pr-2 truncate">
                  {day}
                </div>
                {Array.from({ length: HOURS_PER_TAB }).map((_, offset) => {
                  const idx = di * HOURS_PER_TAB + offset;
                  const hour = state.hours[idx];
                  const filled = hasHourContent(hour, state.categories, state.speech_types);
                  const title = filled
                    ? `${day} · Ώρα ${offset + 1} — Κάνε κλικ για επεξεργασία`
                    : `${day} · Ώρα ${offset + 1} (κενό)`;
                  return (
                    <button
                      key={offset}
                      type="button"
                      data-testid={`overview-mini-clock-${idx}`}
                      disabled={!filled}
                      onClick={() => filled && onHourClick && onHourClick(idx)}
                      title={title}
                      className={
                        filled
                          ? 'cursor-pointer rounded-full hover:ring-2 hover:ring-[#FF9F0A] focus:outline-none focus:ring-2 focus:ring-[#FF9F0A] transition p-0 border-none bg-transparent'
                          : 'opacity-25 cursor-not-allowed p-0 border-none bg-transparent'
                      }
                    >
                      <MiniClock
                        rows={(hour && hour.rows) || []}
                        categories={state.categories}
                        speechTypes={state.speech_types}
                        size={40}
                      />
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact SVG doughnut used inside the mosaic. Renders each segment
// clockwise from 12 o'clock, with a light gap slice for unfilled time.
function MiniClock({ rows, categories, speechTypes, size = 40 }) {
  const { segments } = computeHour(rows, categories, speechTypes);
  const cx = size / 2;
  const cy = size / 2;
  const rO = size / 2 - 1;
  const rI = rO * 0.42;
  const used = segments.reduce((a, s) => a + s.dur, 0);
  const denom = Math.max(used, HOUR_SECONDS);
  const gap = used < HOUR_SECONDS ? HOUR_SECONDS - used : 0;

  let ang = 0;
  const paths = [];
  const push = (dur, color, key) => {
    const a0 = ang;
    const a1 = ang + (2 * Math.PI * dur) / denom;
    ang = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + rO * Math.sin(a0);
    const y0 = cy - rO * Math.cos(a0);
    const x1 = cx + rO * Math.sin(a1);
    const y1 = cy - rO * Math.cos(a1);
    const xi1 = cx + rI * Math.sin(a1);
    const yi1 = cy - rI * Math.cos(a1);
    const xi0 = cx + rI * Math.sin(a0);
    const yi0 = cy - rI * Math.cos(a0);
    paths.push(
      <path
        key={key}
        d={`M${x0} ${y0} A${rO} ${rO} 0 ${large} 1 ${x1} ${y1} L${xi1} ${yi1} A${rI} ${rI} 0 ${large} 0 ${xi0} ${yi0} Z`}
        fill={color}
      />
    );
  };
  segments.forEach((s, i) => push(s.dur, s.color, `s-${i}`));
  if (gap > 0) push(gap, GAP_COLOR, 'gap');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      {paths}
    </svg>
  );
}
